import io, { Socket } from 'socket.io-client'
import browser from './browser'
import { store } from '../store'
import { IP_PRIVATE } from '@common/constants'
import { IBackendState } from '../models/backend'
import { PORT, FRONTEND_RETRY_DELAY } from '../constants'
import { EventEmitter } from 'events'
import network from '../services/Network'

class Controller extends EventEmitter {
  private socket?: Socket
  private retrying?: NodeJS.Timeout
  private credentials?: UserCredentials
  private url: string = '/'
  handlers: ILookup<(result: any) => void> = {}

  init() {
    const { protocol, host } = window.location
    const isDev = host === `${IP_PRIVATE}:3003`
    this.url = protocol === 'file:' || isDev ? `http://${IP_PRIVATE}:${PORT}` : '/'
    this.onNetworkConnect()
    if (!navigator.onLine) network.offline()
    network.on('connect', this.onNetworkConnect)
    // Deliberately no 'disconnect' listener. This socket talks to the backend on
    // 127.0.0.1, which stays reachable when internet access drops - closing it
    // would blind the UI to local connection state for no reason. Losing the local
    // backend is a different failure, and socket.io's own reconnection covers it.
  }

  log(...args) {
    console.log(`%c${args[0]}`, 'color:limegreen;font-weight:bold', ...args.slice(1))
  }

  onNetworkConnect = () => {
    const state = store.getState()
    const { ui, auth } = store.dispatch

    if (!navigator.onLine) return
    // Keyed off the user, not backendAuthenticated or authenticated:
    //  - backendAuthenticated is cleared by a dropped socket (auth.disconnect), so
    //    waking from sleep took the auth.init() branch, which no-ops once a user
    //    is in state - the re-connect nudge never actually ran.
    //  - authenticated is set before fetchUser, so it stays true when graphQLLogin
    //    fails, leaving no user and no socket. Reopening nothing would strand that
    //    sign in until a reload; it needs the auth.init() retry instead.
    // A user means sign in completed and setupConnection made a socket to reopen.
    // auth isn't persisted, so this is false at startup and initial sign in still
    // takes the else branch.
    if (state.auth.user) {
      this.log('-- ONLINE AUTHORIZED RE-CONNECT')
      this.open()
    } else {
      this.log('1- ONLINE AUTHORIZE AND CONNECT')
      ui.set({ errorMessage: '' })
      // This is the app's only entry into auth.init, so it has to run whether or
      // not the window has focus - a window launched in the background still has
      // to sign in. Unattended, though, nobody asked for this and nobody is
      // watching, so a failed session check shouldn't leave a toast waiting.
      auth.init({ silent: !network.isActive() })
    }
  }

  setupConnection(credentials: UserCredentials) {
    this.credentials = credentials
    this.handlers = getEventHandlers()

    if (!browser.hasBackend) return

    // Re-auth (token refresh, or signing back in) calls this again. Drop the
    // previous socket first - listeners are removed before closing so its
    // disconnect handler doesn't fire, and its handlers can't double dispatch
    // every backend event onto the new connection's.
    if (this.socket) {
      this.log('REPLACING LOCAL SOCKET')
      this.socket.removeAllListeners()
      this.socket.close()
    }

    this.socket = io(this.url, {
      transports: ['websocket'],
      forceNew: true,
      reconnectionDelay: FRONTEND_RETRY_DELAY,
    })

    this.logErrors()

    for (const eventName in this.handlers) {
      if (this.handlers.hasOwnProperty(eventName)) {
        const name = eventName as SocketEvent
        const handler = this.handlers[name]
        this.on(name, handler)
      }
    }
  }

  logErrors() {
    for (const errorEvent of ['error', 'reconnect_error', 'connect_error']) {
      this.socket?.on(errorEvent, error => {
        this.log(errorEvent.toUpperCase(), error.message)
        store.dispatch.ui.set({
          offline: { title: 'Network Error', message: error.message, severity: 'error' },
        })
      })
    }
  }

  auth() {
    if (browser.hasBackend) emit('authentication', this.credentials)
  }

  // Retry open with delay, force skips delay
  open(retry?: boolean, force?: boolean) {
    // Nothing to open without a local backend (portal/mobile) - setupConnection
    // never made a socket there, so this would only schedule a timeout to clear
    // the offline notice as a side effect.
    if (!browser.hasBackend) return
    if (force || (navigator.onLine && !this.socket?.connected && !this.retrying)) {
      // force skips the !retrying guard above, so drop any pending retry rather
      // than orphan its timer - it would fire up to FRONTEND_RETRY_DELAY later
      clearTimeout(this.retrying)
      this.retrying = setTimeout(
        () => {
          this.log('Retrying local socket.io connection')
          this.retrying = undefined
          this.socket?.open()
          store.dispatch.ui.set({ offline: undefined })
        },
        retry ? FRONTEND_RETRY_DELAY : 0
      )
    }
  }

  // arrow so it stays bound if passed as a callback
  close = () => {
    this.log('CLOSE LOCAL SOCKET')
    this.socket?.close()
  }

  on(eventName: SocketEvent, handler: (...args: any[]) => void) {
    this.socket?.on(eventName, handler)
    return this
  }

  off(eventName: SocketEvent) {
    this.socket?.off(eventName)
    return this
  }

  emit = (event: SocketAction, ...args: any[]): boolean => {
    if (!this.socket?.connected) {
      if (browser.hasBackend) this.log('EMIT CANCELED - LOCAL SOCKET DISCONNECTED', event, ...args)
      return false
    }
    this.log('Controller emit', event, ...args)
    this.socket?.emit(event, ...args)
    return true
  }

  // Force an immediate reopen and resolve once the backend has authenticated the
  // new connection, false if it doesn't come back within timeout. socket.io's own
  // retry waits FRONTEND_RETRY_DELAY, far too long to hold a sign out on.
  reconnectNow = (timeout: number): Promise<boolean> =>
    new Promise(resolve => {
      const socket = this.socket
      if (!browser.hasBackend || !socket) return resolve(false)

      let timer: NodeJS.Timeout | undefined
      const finish = (result: boolean) => {
        clearTimeout(timer)
        socket.off('authenticated', onAuthenticated)
        this.log('RECONNECT NOW', result ? 'AUTHENTICATED' : 'TIMED OUT')
        resolve(result)
      }
      const onAuthenticated = () => finish(true)

      socket.on('authenticated', onAuthenticated)
      timer = setTimeout(() => finish(false), timeout)
      this.open(false, true)
    })
}

type EventHandlers = { [event: string]: (data?: any) => any }

function getEventHandlers() {
  const { connections, binaries, auth, backend, ui } = store.dispatch

  return {
    connect: () => {
      controller.log('event: CONNECT LOCAL SOCKET')
      controller.auth()
      ui.set({ connected: true })
    },

    unauthorized: (error: Error) => auth.backendSignInError(error.message),

    authenticated: () => {
      controller.log('event: BACKEND AUTHENTICATED')
      auth.backendAuthenticated()
    },

    appReady: () => {
      controller.log('event: DATA READY')
      auth.appReady()
    },

    disconnect: () => {
      controller.log('event: DISCONNECT')
      auth.disconnect()
    },

    'signed-out': () => auth.signedOut(),

    pool: (result: IConnection[]) => {
      controller.log('event: socket pool', result)
      connections.restoreConnections(result)
    },

    updated: (result: IConnection) => {
      controller.log('event: socket connection', result)
      connections.updateConnection(result)
    },

    device: (result: string) => {
      controller.log('event: socket device', result)
      backend.targetDeviceUpdated(result)
    },

    scan: (result: IScanData) => {
      controller.log('event: socket scan', result)
      if (result) backend.set({ scanData: result })
    },

    interfaces: (result: IInterface[]) => {
      controller.log('event: socket interfaces', result)
      if (result) backend.set({ interfaces: result })
    },

    freePort: (result: number) => backend.set({ freePort: result }),

    reachablePort: (result: boolean) => {
      backend.set({ reachablePort: result ? 'REACHABLE' : 'UNREACHABLE' })
    },

    environment: (result: IBackendState['environment']) => {
      backend.set({ environment: result })
    },

    preferences: (result: IPreferences) => backend.set({ preferences: result }),

    'app/filePath': (filePath: string) => backend.set({ filePath }),

    // AutoUpdate
    'update/status': (updateStatus: IBackendState['updateStatus']) => {
      backend.set({ updateStatus })
    },

    'cli/error': error => {
      ui.set({ errorMessage: error })
      ui.updated()
    },

    'binary/install/error': (error: string) => binaries.installError(error),
    'binary/install/progress': (progress: number) => controller.log('event: binary/install/progress', progress),
    'binary/installed': (info: InstallationInfo) => binaries.installed(info),
    'binary/not-installed': (status: BinaryReason) => binaries.notInstalled(status),
  } as EventHandlers
}

const controller = new Controller()
export default controller
export const emit = controller.emit
